<template>
  <div id="MatchProfileAnalysis" class="row match-profile-analysis">
    <div class="chart-segment-options align-items-start col-2 mt-0">
      <modal-link modal-id="Dynamic Insights" :content-list="pageContent"></modal-link>
      <a href="#!" v-show="!displayMatchProfileAnalysis" @click="toggleMatchProfileAnalysis">
        Show Dynamic Insights<i class="fa fa-chevron-down ml-1"></i>
      </a>
      <a href="#!" v-show="displayMatchProfileAnalysis" @click="toggleMatchProfileAnalysis">
        Hide Dynamic Insights<i class="fa fa-chevron-up ml-1"></i>
      </a>
    </div>
    <div v-show="displayMatchProfileAnalysis && !isVerbatimLocked" class="match-profile-analysis-section col-10">
      <div class="match-profile-analysis-intro text-primary">
        Instantly map value & behavioral similarities, differences, or shifts between segments or time periods.
        Select which analysis type & parts to include for generating key insights.
      </div>
      <div class="match-profile-analysis-options pb-2">
        <div class="chart-criteria-row">
          <div class="match-profile-analysis-label-column">
            <label class="sub-heading-label text-primary">Insight Type</label>
          </div>
          <div class="match-profile-analysis-field-column">
            <div class="form-group mb-1">
              <input
                id="variationAnalysis"
                name="insightType"
                type="radio"
                value="variation"
                v-model="insightType"
              />
              <span class="ml-1 mb-0">Variation Analysis (similarities and behavioral differences across groups)</span>
            </div>
            <div class="form-group mb-0">
              <input
                id="conversionAnalysis"
                name="insightType"
                type="radio"
                value="conversion"
                v-model="insightType"
              />
              <span class="ml-1 mb-0">Conversion Analysis (levers and value shifts to transition first group to second group)</span>
            </div>
          </div>
        </div>

        <div class="chart-criteria-row">
          <div class="match-profile-analysis-label-column">
            <label class="sub-heading-label text-primary">Based On</label>
          </div>
          <div class="match-profile-analysis-field-column match-profile-analysis-checkbox-grid">
            <div class="form-group">
              <input id="includeValueGapsForInsights" name="includeValueGapsForInsights" type="checkbox" true-value="Y" false-value="N" v-model="includeValueGaps" />
              <span class="ml-1 mr-3 mb-0">Value Gaps</span>
            </div>
            <div class="form-group">
              <input id="includeExpectationsForInsights" name="includeExpectationsForInsights" type="checkbox" true-value="Y" false-value="N" v-model="includeExpectations" />
              <span class="ml-1 mr-3 mb-0">Expectations</span>
            </div>
            <div class="form-group">
              <input id="includePerformanceForInsights" name="includePerformanceForInsights" type="checkbox" true-value="Y" false-value="N" v-model="includePerformance" />
              <span class="ml-1 mr-3 mb-0">Performance</span>
            </div>
            <div class="form-group">
              <input id="includeWillingnessForInsights" name="includeWillingnessForInsights" type="checkbox" true-value="Y" false-value="N" v-model="includeWillingnessToPay"/>
              <span class="ml-1 mr-3 mb-0">Willingness %s</span>
            </div>
            <div class="form-group">
              <input id="includeRelativeStrengthForInsights" name="includeRelativeStrengthForInsights" type="checkbox" true-value="Y" false-value="N" v-model="includeRelativeStrength"/>
              <span class="ml-1 mr-3 mb-0">Relative Strength</span>
            </div>
            <div class="form-group">
              <input id="includeSentimentThemesForInsights" name="includeSentimentThemesForInsights" type="checkbox" true-value="Y" false-value="N" v-model="includeSentimentThemes" />
              <span class="ml-1 mr-3 mb-0">Sentiment Themes</span>
            </div>
          </div>
        </div>

        <div class="chart-criteria-row">
          <div class="match-profile-analysis-label-column">
            <label class="sub-heading-label text-primary"># of Insights</label>
          </div>
          <div class="match-profile-analysis-field-column d-flex align-items-center flex-wrap">
            <div class="match-profile-analysis-select-width mr-3">
              <select
                id="number_of_recommendations"
                name="number_of_recommendations"
                class="custom-select shadow w-100"
                v-model="numberOfKeyTakeaways"
              >
                <option value="0">Select</option>
                <option v-for="(option, idxNumberOfKeyTakeawayOptions) in numberOfKeyTakeawayOptions" v-bind:value="option.value" v-bind:key="idxNumberOfKeyTakeawayOptions">
                  {{ option.label }}
                </option>
              </select>
            </div>

            <label class="sub-heading-label text-primary m-0 pr-2">AI Toolset</label>
            <div class="match-profile-analysis-select-width">
              <select
                id="ai_type_id"
                name="ai_type_id"
                class="custom-select shadow w-100"
                v-model="matchProfileAnalysisAiTypeId"
              >
                <option v-for="(aiTypeOption, idxAiTypeOptions) in ai_type_options" v-bind:value="aiTypeOption.aiTypeId" v-bind:key="idxAiTypeOptions">
                  {{ aiTypeOption.displayName }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <button class="btn btn-primary generate-insights-btn" @click="generateInsights" :disabled="(processInitiated && !processingComplete)">
        <span>Generate Insights</span>
        <span v-show="processInitiated && !processingComplete" class="spinner-border spinner-border-sm ml-2"></span>
      </button>
    </div>
    <div class="w-100 row justify-content-center pt-4" v-if="showResults && displayMatchProfileAnalysis">
      <div class="w-100">
        <span class="match-profile-analysis-advisory-note">
          AI-generated Key Insights are for advisory purposes <u>only, and</u> should be used as a starting point for your roadmap.
        </span>
      </div>
      <div id="insights-data-container" class="match-profile-analysis-results">
        <div class="chart-header-container d-flex">
          <div class="d-flex title w-100" style="padding-left: 0 !important;">
            <h4 class="text-primary mt-1 mb-0">
              {{ title }}
            </h4>
          </div>
        </div>
        <div class="pt-2">
          <button type="button" class="btn btn-primary ml-auto" data-html2canvas-ignore="true" @click="$modal.show('print-instant-report-modal')">Save Report</button>
        </div>
        <table id="insights-detailed-data" class="recommendations-table" cellspacing="0" style="padding-left: 0 !important;">
          <thead class="align-middle">
            <!-- Invisible helper row that occupies exactly 0px -->
            <tr style="height: 0; line-height: 0; border: none;">
              <th style="padding: 0; margin: 0; border: none; height: 0;"></th>
              <th v-if="displayInsightType === 'variation'" style="padding: 0; margin: 0; border: none; height: 0;"></th>
              <th style="padding: 0; margin: 0; border: none; height: 0;"></th>
              <th style="padding: 0; margin: 0; border: none; height: 0;"></th>
              <th v-if="displayInsightType === 'conversion'" style="padding: 0; margin: 0; border: none; height: 0;"></th>
              <th style="padding: 0; margin: 0; border: none; height: 0;"></th>
              <th style="padding: 0; margin: 0; border: none; height: 0;"></th>
              <th v-if="savedCard3Name != ''" style="padding: 0; margin: 0; border: none; height: 0;"></th>
              <th v-if="savedCard4Name != ''" style="padding: 0; margin: 0; border: none; height: 0;"></th>
            </tr> 
            <tr>
                <th :colspan="totalDisplayColumns" class="bg-white align-left pb-2 text-primary">
                  <span class="recommendations-summary-title">{{ displayInsightType === 'variation' ? 'Variation' : 'Conversion' }} Summary - &nbsp;</span>
                  <span class="recommendations-summary-text">{{ summary }}</span>
                </th>
              </tr>
              <tr>
                  <th class="align-middle text-center" style="padding-right: 0px !important">#</th>
                  <th class="align-middle" v-if="displayInsightType === 'variation'">Type</th>
                  <th class="align-middle">Value Source</th>
                  <th class="align-middle">Key Insight</th>
                  <th class="align-middle" v-if="displayInsightType === 'conversion'">Action to Convert</th>
                  <th class="align-middle">{{ savedCard1Name }}</th>
                  <th class="align-middle">{{ savedCard2Name }}</th>
                  <th class="align-middle" v-if="savedCard3Name != ''">{{ savedCard3Name }}</th>
                  <th class="align-middle" v-if="savedCard4Name != ''">{{ savedCard4Name }}</th>
              </tr>                    
          </thead>
          <tbody></tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import ViewMixins from "../../mixins/ViewMixins.vue";
import ErrorService from "../../services/error.service";
import AIService from "../../services/ai.service";
import SurveyService from "../../services/survey.service";
import UserService from "../../services/user.service";
import AdminService from "../../services/admin.service";
import TableService from "../../services/table.service";
import ReportService from "../../services/report.service";
export default {
  name: "MatchProfileAnalysis",
   mixins: [
    ViewMixins
   ],
  props: {
      clientObject: {
          type: Object,
          default: function() {
              return {};
          }
      },    
      clientChart: {
          type: Object,
          default: function() {
              return {};
          }
      },
      entrySurveyCode: {
          type: String,
          default: SurveyService.getSurveyCode()
      },
      criteriaResultsCompanyList: {
          type: Array,
          default: function() {
              return [];
          }
      },
      clientSettings: {
          type: Object,
          default: () => ({
              aiTypeId: null
          })
      },
      recommendationsData: {
          type: Object,
          default: () => null
      },
      comparativeSavedCards: {
          type: Array,
          default: () => []
      },
  },
  data() {
    return {
      title: "Dynamic Insights",
      displayMatchProfileAnalysis: false,
      insightType: "variation",
      displayInsightType: "variation",
      includeValueGaps: "Y",
      includeExpectations: "Y",
      includePerformance: "Y",
      includeWillingnessToPay: "N",
      includeRelativeStrength: "N",
      includeSentimentThemes: "N",
      showResults: false,
      numberOfKeyTakeawayOptions: [
        { label: "1", value: 1 },
        { label: "2", value: 2 },
        { label: "3", value: 3 },
        { label: "4", value: 4 },
        { label: "5", value: 5 },
        { label: "10", value: 10 },
        { label: "15", value: 15 },
        { label: "All", value: 17 },
      ],
      numberOfKeyTakeaways: 0,
      matchProfileAnalysisAiTypeId: 0,
      ai_type_options: [],
      topResults: [],
      topAttributeRecommendations: [],
      savedCard1Name: "",
      savedCard2Name: "",
      savedCard3Name: "",
      savedCard4Name: "",
      AUTO_SWITCH_LLM_MESSAGE: "Due to high demand, selected AI Model could not process your request. Results are generated using an alternative model.",
      processInitiated: false,
      processingComplete: true,  
      isSavedCard: false,
      summary: "",   
      table: {
        id: "insights-detailed-data",
        class: "table display wrap row-border table-hover",
        configuration: {
            dom: '<"w-100"<"d-flex"><B>>frtip',      
            searching: false,
            paging: false,
            pageLength: 25,
            select: true,
            processing: false,
            responsive: false,
            destroy: true,
            info: false,
            order: [],
            ordering: false,
            keys: true,
            sort: false,
            bSortable: false,
            bSort: false,
            buttons: [],
        },
      },      
    };
  },
  computed: {
    noInputSelected() {
      return this.includeValueGaps == "N"
        && this.includeExpectations == "N"
        && this.includePerformance == "N"
        && this.includeWillingnessToPay == "N";
    },
    noOfRespondents: function() {
      let returnNumber = 0;
      let resultClientList = this.criteriaResultsCompanyList.find(x => x.type === "client");
      if (typeof resultClientList !== "undefined" && resultClientList.list.length > 0) {
        returnNumber = resultClientList.list[0].numberOfResults;
      }
      return returnNumber;
    },
    totalDisplayColumns() {
      let count = 5; // Base static columns: #, Value Source, Key Insight, Card 1, Card 2
      
      if (this.displayInsightType === 'variation') count++;
      if (this.displayInsightType === 'conversion') count++;
      if (this.savedCard3Name !== '') count++;
      if (this.savedCard4Name !== '') count++;
      
      return count;
    },
    isVerbatimLocked() {
      return this.clientSettings.aiTypeId == 0;
    }
  },
  mounted() {
    this.loadAITypes();
    this.displayMatchProfileAnalysis = (this.isVerbatimLocked || !this.processInitiated) ? false : true;
  },
  methods: {
    createTopAttributeRecommendationsData(allData) {
        const valueSources = [];
        for (let i = 0; i < allData.length; i++) {
            const recommendation = allData[i];
            const valueSource = recommendation.valueSource || "";
            if (valueSource == "") {
                continue;
            }
            valueSources.push(valueSource);
        }
        this.topAttributeRecommendations = valueSources;
        this.$emit("top-attribute-recommendations", this.topAttributeRecommendations, this.insightType);
    },
    loadAITypes() {
        AIService.getAllActiveAITypes(this.setAITypeOptions);
    },
    setAITypeOptions(response) {
        this.ai_type_options = response;
        if (!this.isSavedCard && this.isMissingAiTypeId(this.matchProfileAnalysisAiTypeId)) {
            this.setDefaultAiTypeIdFromClientSettings();
        }
    },
    isMissingAiTypeId(value) {
      return value == "" || value == null || value == 0;
    },
    getClientSettingsAiTypeId() {
      if (typeof this.clientSettings.aiTypeId != "undefined" && this.clientSettings.aiTypeId != null) {
        return this.clientSettings.aiTypeId;
      }
      return 0;
    },
    setDefaultAiTypeIdFromClientSettings() {
      const clientSettingsAiTypeId = this.getClientSettingsAiTypeId();
      if (!this.isMissingAiTypeId(clientSettingsAiTypeId)) {
        this.matchProfileAnalysisAiTypeId = clientSettingsAiTypeId;
      }
    },
    toggleMatchProfileAnalysis() {
      if (this.isVerbatimLocked) {
        AdminService.displayErrorMessage({
          text: "Dynamic Insights is not active for your organization. Contact your W Ratings Advisor for more information."
        });
        return;
      }
      this.displayMatchProfileAnalysis = !this.displayMatchProfileAnalysis;
    },
    hasInvalidSavedCardCount() {
      const comparativeSavedCards = this.getComparativeSavedCards();
      if (this.insightType == "conversion" && comparativeSavedCards.length !== 2) {
        AdminService.displayErrorMessage({
          text: "Conversion Analysis requires exactly 2 saved cards."
        });
        return true;
      }
      if (this.insightType == "variation" && (comparativeSavedCards.length < 2 || comparativeSavedCards.length > 4)) {
        AdminService.displayErrorMessage({
          text: "Variance Analysis requires 2 to 4 saved cards."
        });
        return true;
      }
      return false;
    },
    hasRespondentRecords() {
      let responsesFound = false;
      if (this.recommendationsData && this.recommendationsData?.cardList) {
        const cardList = this.recommendationsData?.cardList || [];

        const savedCardsFromCardList = cardList.filter(card =>
          this.comparativeSavedCards.some(savedCard => savedCard?.id === card?.id)
        );

        responsesFound = savedCardsFromCardList.some(
          card => card?.numberOfRespondentRecords > 0
        );
      }
      if(!responsesFound) {
        AdminService.displayErrorMessage({
          text: "No chart data found for the saved cards."
        });
        return false;
      }
      return true;
    },
    getComparativeSavedCards() {
      return this.comparativeSavedCards || [];
    },
    buildInsightsParams() {
      const comparativeSavedCards = this.getComparativeSavedCards();
      let params = new URLSearchParams();
      params.append("client_id", this.clientObject.clientId);
      params.append("survey_id", SurveyService.getSurveyId());
      params.append("user_id", UserService.getUserId());
      params.append("include_value_gaps", this.includeValueGaps);
      params.append("include_expectations", this.includeExpectations);
      params.append("include_performance", this.includePerformance);
      params.append("include_willingness_to_pay", this.includeWillingnessToPay);
      params.append("include_relative_strength", this.includeRelativeStrength);
      params.append("include_sentiment_themes", this.includeSentimentThemes);
      // params.append("chart_data", JSON.stringify(this.buildRecommendationsData()));
      params.append("number_of_key_takeaways", this.numberOfKeyTakeaways);
      params.append("number_of_respondents", this.noOfRespondents);
      params.append("analysis_type", this.insightType);
      if (this.insightType == "conversion") {
        params.append("base_saved_card_id", comparativeSavedCards[0]?.clientChartId || "");
        params.append("comparison_saved_card_ids", comparativeSavedCards[1]?.clientChartId || "");
      } else {
        params.append("base_saved_card_id", "");
        comparativeSavedCards.forEach(card => params.append("comparison_saved_card_ids", card.clientChartId));
      }
      params.append("ai_type_id", this.matchProfileAnalysisAiTypeId);
      return params;
    },
    async generateInsights() {
      if (this.hasInvalidSavedCardCount()) {
        return;
      }
      if (this.noOfRespondents == null || this.noOfRespondents <= 0) {
          AdminService.displayErrorMessage({ text: "Please ensure there are respondents for the selected criteria before generating insights." });
          return;
      }
      if (!this.hasRespondentRecords()) {
        return;
      }
      if (this.noInputSelected) {
          AdminService.displayErrorMessage({ text: "Please select at least one option - Value Gaps, Expectations, Performance, Willingness %s before generating insights." });
          return;
      }
      if (this.isMissingAiTypeId(this.matchProfileAnalysisAiTypeId)) {
        this.setDefaultAiTypeIdFromClientSettings();
        if (this.isMissingAiTypeId(this.matchProfileAnalysisAiTypeId)) {
          // AdminService.displayErrorMessage({ text: "Please select AI Toolset to generate recommendations." });
          return;
        }
      }
      this.processingComplete = false;
      this.processInitiated = true;
      this.showResults = false;
      this.topResults = [];
      this.topAttributeRecommendations = [];
      this.$emit("top-attribute-recommendations", []);
      this.$root.processServerRequest("Generating Match Profile Analysis Insights");
      let params = this.buildInsightsParams();
      params.append("chart_data", JSON.stringify(this.buildRecommendationsData()));
      await AIService.generateMatchProfileAnalysis(params, this.generateMatchProfileAnalysisCallback);
    },
    saveToCard: function() {
      let params = this.buildInsightsParams();
      params.append("auto_process_match_profile_analysis_by_ai", true);
      this.$emit("save-match-profile-analysis-criteria", params);
    },
    loadCriteriaData() {
      let params = new URLSearchParams(this.clientChart.criteria);
      let autoProcessMatchProfileAnalysisByAI = params.get("auto_process_match_profile_analysis_by_ai") || false;
      if (autoProcessMatchProfileAnalysisByAI == "true" || autoProcessMatchProfileAnalysisByAI == true) {
        this.insightType = params.get("analysis_type") || "variation";
        this.includeValueGaps = params.get("include_value_gaps") || "Y";
        this.includeExpectations = params.get("include_expectations") || "Y";
        this.includePerformance = params.get("include_performance") || "Y";
        this.includeWillingnessToPay = params.get("include_willingness_to_pay") || "N";
        this.includeRelativeStrength = params.get("include_relative_strength") || "N";
        this.includeSentimentThemes = params.get("include_sentiment_themes") || "N";
        this.numberOfKeyTakeaways = Number(params.get("number_of_key_takeaways")) || 0;
        const criteriaAiTypeId = params.get("ai_type_id");
        if (!this.isMissingAiTypeId(criteriaAiTypeId)) {
          this.matchProfileAnalysisAiTypeId = criteriaAiTypeId;
        } else if (!this.isSavedCard) {
          this.setDefaultAiTypeIdFromClientSettings();
        }

        this.displayMatchProfileAnalysis = true;
        if (this.getComparativeSavedCards().length > 0) {
          this.generateInsights();
        }
      }
    },
    resetValues() {
      this.destroyDt("#insights-detailed-data");
      this.showResults = false;
      this.displayMatchProfileAnalysis = false;
      this.insightType = "variation";
      this.displayInsightType = "variation";
      this.includeValueGaps = "Y";
      this.includeExpectations = "Y";
      this.includePerformance = "Y";
      this.includeWillingnessToPay = "N";
      this.includeRelativeStrength = "N";
      this.includeSentimentThemes = "N";
      this.numberOfKeyTakeaways = 0;
      if (typeof this.clientSettings.aiTypeId != "undefined" && this.clientSettings.aiTypeId != null){
        this.matchProfileAnalysisAiTypeId = this.clientSettings.aiTypeId; 
      } else {
        this.matchProfileAnalysisAiTypeId = 0; 
      }
      this.topResults = [];
      this.topAttributeRecommendations = [];
      this.$emit("top-attribute-recommendations", []);
      this.summary = "";
      this.isSavedCard = false;
    },
    generateMatchProfileAnalysisCallback(response) {
      let statusCode = response.status;
      this.displayMatchProfileAnalysis = true;
      if (statusCode == 200) {
                          
          const data = response.data.data || {};
          const savedCardNames = data?.savedCardNames || [];
          this.savedCard1Name = savedCardNames[0] ? savedCardNames[0] : "";
          this.savedCard2Name = savedCardNames[1] ? savedCardNames[1] : "";
          this.savedCard3Name = savedCardNames[2] ? savedCardNames[2] : "";
          this.savedCard4Name = savedCardNames[3] ? savedCardNames[3] : "";
          
          const response_ai_type_id = (data?.ai_type) ? (data?.ai_type.aiTypeId) : 0;
          if(response_ai_type_id != this.matchProfileAnalysisAiTypeId) {
              AdminService.displayInfoMessage({ text: this.AUTO_SWITCH_LLM_MESSAGE}, { duration: 8000 }); // can be appended with - data?.ai_type.displayName 
          }
          this.matchProfileAnalysisAiTypeId = response_ai_type_id;

          this.saveToCard();
          
          this.topResults = data.topResults || [];
          this.createTopAttributeRecommendationsData(data.topResults || []);
          this.summary = data.summary || "";
          this.displayInsightType = data.analysisType || this.insightType;
          this.showResults = true;
          this.$nextTick(() => {
            this.initResultTables(this.topResults);
          });
          this.$emit("set-default-title",  this.displayInsightType, savedCardNames);
		      this.isSavedCard = false;
      } else {
          AdminService.displayErrorMessage({ text: response.data.message });
          this.topAttributeRecommendations = [];
          this.$emit("top-attribute-recommendations", []);
          this.showResults = false;
          this.initResultTables([]);
      }
      this.$root.processServerResponse();
      this.processInitiated = false;
      this.processingComplete = true;
      this.isSavedCard = false;
    },
    buildRecommendationsData() {
      const data = this.recommendationsData;
      
      const selectedCols = this.getSelectedColumnsForRecommendations(data);
      const colIndexes = this.getColumnIndexes(data, selectedCols);
      const filteredRows = this.getFilteredRows(data, colIndexes);

      return {
          cols: selectedCols, //headers
          rows: filteredRows,
          isPositiveCorrelation: data.isPositiveCorrelation
      };
    },
    getSelectedColumnsForRecommendations(data) {
      if (!data?.cols) return;
      const selectedCols = [];
      data.cols.forEach((col, index) => {

        if (col.id === "need") {
            selectedCols.push({ ...col, sourceIndex: index });
        }
        if (this.includeValueGaps === "Y" && col.id === "gap") {
            selectedCols.push({
              ...col,
              sourceIndex: index,
              label: col.label.replace(/^GAP\s*\(/i, "ValueGap (")
          });
        }
        if (this.includeExpectations === "Y" && col.id === "customer-expectations") {
            selectedCols.push({
              ...col,
              sourceIndex: index,
              label: col.label.replace(/^EXP\s*-\s*/i, "Expectation - ")
          });
        }
        if (this.includePerformance === "Y" && col.id === "performance") {
            selectedCols.push({ 
              ...col,
              sourceIndex: index,
              label: "Performance - " + col.label
           });
        }
        if (col.id === "wtp") {
            selectedCols.push({ ...col, sourceIndex: index });
        }
      });
      return selectedCols;
    },
    getColumnIndexes(data, selectedCols) {
      return selectedCols.map(sc => sc.sourceIndex);
    },
    getFilteredRows(data, colIndexes) {
      const needLookup = {};
      (data.emotionalNeedNamesMap || []).forEach(n => {
          needLookup[n.needName] = n.dimensionName;
      });
      
      return data.rows.map(row => {
        return colIndexes.map(i => {
          const cell = row.c[i];
          let value = cell ? cell.v : '';
          const colId = data.cols[i].id;
          if (colId === "pricing-power" && value !== '') {
              value = value + "%";
          } else if (colId === "need" && value !== '') {
              value = needLookup[value] || value; // replace if mapping exists
          } else {
              let numVal = parseFloat(value);
              if (typeof numVal === 'number' && !isNaN(numVal)) {
                  value = numVal.toFixed(2); 
              }
          }
          return value;
        });
      });
    },
    initResultTables(all = []) {
      const posSel = "#insights-detailed-data";

      this.destroyDt(posSel);
      $(posSel).find("tbody").empty();

      if (this.displayInsightType == "variation") {
        this.table.columns =
          TableService.getMatchVarianceAnalysisResultsTableAttributes(
            this.savedCard3Name,
            this.savedCard4Name
          );
      } else {
        this.table.columns =
          TableService.getMatchConversionAnalysisResultsTableAttributes();
      }

      const baseCfg = $.extend(true, {}, this.table.configuration);
      const cols = this.table.columns;

      const cfgPos = $.extend(true, {}, baseCfg, {
        data: all,
        columns: cols,
        columnDefs: [
          {
            targets: 0,
            createdCell: function(td) {
              $(td).css("text-align", "center");
            }
          }
        ]
      });

      $.extend(
        cfgPos,
        TableService.getTableButtons(
          this,
          "insights-data-container",
          undefined,
          undefined,
          false,
          "Insights"
        )
      );

      this.$nextTick(() => {
        this.dtPositive = $(posSel).DataTable(cfgPos);
        this.dtPositive.columns.adjust().draw();
      });
    },
    destroyDt(selector) {
      if ($.fn.dataTable.isDataTable(selector)) {
        const dt = $(selector).DataTable();
        dt.clear();
        dt.destroy();
      }

      $(selector).find("tbody").empty();
    },
    async saveDataForInstantReport(clientReportId) {
        await this.$nextTick();
        const clientId = this.clientObject.clientId;
        const tableData = this.getRecommendationsTableData();

        await Promise.all([
            ReportService.saveReportTableFileForInstantReport(tableData, "Table - Insights", clientId, clientReportId, this.saveReportTableFileCallback, this),
            ReportService.saveInstantReportPage(clientId, TableService.getReportTableContentByTable("insights-detailed-data"), "Table - Insights", clientReportId, this.title, this.saveReportPageCallback, this)
        ]);
    },
    getRecommendationsTableData() {
        const tableSelector = "#insights-detailed-data";
        if ($.fn.dataTable.isDataTable(tableSelector)) {
            const dataTable = $(tableSelector).DataTable();
            return TableService.getTableData(dataTable);
        }
        return [];
    },

    saveReportTableFileCallback(response) {},
    saveReportPageCallback(response) {},
  },
  watch: {
    clientChart: function() {
      if (this.clientChart != null && this.clientChart.clientChartId > 0) {
        let params = new URLSearchParams(this.clientChart.criteria);
        this.isSavedCard = params.get("auto_process_match_profile_analysis_by_ai") === "true";

        if (this.isSavedCard) {
            this.displayMatchProfileAnalysis = true;
            this.loadCriteriaData();
        } else {
            this.processingComplete = false;    
        }
      } else {
          this.resetValues();
          this.isSavedCard = false;
          this.processingComplete = false;
      }
    },
    // insightType: function(newVal) {
    //   this.hasInvalidSavedCardCount();
    // },
    clientSettings() {
      if (!this.isSavedCard && this.isMissingAiTypeId(this.matchProfileAnalysisAiTypeId)) {
        this.setDefaultAiTypeIdFromClientSettings();
      }
    },
    entrySurveyCode: function(newVal, oldVal) {
      this.isSavedCard = false;
      this.resetValues();
    },
    comparativeSavedCards: function(newVal) {
      if (newVal && newVal.length > 0) {
        if (this.isSavedCard) {
          this.generateInsights();
        }
      }
    },
  }
};
</script>
